import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Badge, Empty, categoryTone } from "@/components/admin/Ui";
import { Sparkle, ChatCircleDots } from "@phosphor-icons/react";

export default function Replies() {
  const [leads, setLeads] = useState([]);
  const [list, setList] = useState([]);
  const [selectedLead, setSelectedLead] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [r, l] = await Promise.all([api.get("/replies"), api.get("/leads")]);
    setList(r.data.replies || []);
    setLeads(l.data.leads || []);
  };

  useEffect(() => { load(); }, []);

  const classify = async () => {
    if (!text.trim()) { toast.error("Paste reply text"); return; }
    setBusy(true);
    try {
      await api.post("/replies/classify", { lead_id: selectedLead, raw_text: text, from_email: fromEmail });
      toast.success("Classified");
      setText(""); setFromEmail(""); setSelectedLead("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Classify failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-replies-page">
      <PageHeader title="Reply Handling" subtitle="Agent 4 · GPT-5-mini" />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 bg-white border border-black/10 p-6 space-y-4">
          <div className="overline">New reply</div>
          <select
            data-testid="reply-lead-select"
            value={selectedLead}
            onChange={(e) => setSelectedLead(e.target.value)}
            className="w-full border border-black/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy"
          >
            <option value="">(Optional) attach to lead…</option>
            {leads.map((l) => (<option key={l.id} value={l.id}>{l.company_name}</option>))}
          </select>
          <input
            data-testid="reply-from-email"
            value={fromEmail}
            onChange={(e) => setFromEmail(e.target.value)}
            placeholder="From email"
            className="w-full border border-black/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy"
          />
          <textarea
            data-testid="reply-text"
            rows={10}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste the full reply text here…"
            className="w-full border border-black/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy"
          />
          <button
            data-testid="reply-classify-button"
            onClick={classify}
            disabled={busy}
            className="btn-primary w-full justify-center"
          >
            <Sparkle size={14} /> {busy ? "Classifying…" : "Classify with AI"}
          </button>
        </div>

        <div className="lg:col-span-7 space-y-3">
          {list.length === 0 ? <Empty label="No replies classified yet." /> : list.map((r) => (
            <div key={r.id} className="bg-white border border-black/10 p-5" data-testid={`reply-item-${r.id}`}>
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-2 text-sm text-navy/70">
                  <ChatCircleDots size={14} weight="duotone" className="text-ember" />
                  {r.from_email || "unknown sender"}
                </div>
                <div className="flex items-center gap-2">
                  <Badge tone={categoryTone(r.category)}>{r.category}</Badge>
                  <span className="text-xs text-navy/50">conf {(r.confidence * 100).toFixed(0)}%</span>
                </div>
              </div>
              <div className="text-sm text-navy/80 whitespace-pre-wrap border-l-2 border-navy/20 pl-3 my-3">{r.raw_text}</div>
              <div className="text-xs text-navy/60"><span className="font-semibold text-navy">Action: </span>{r.recommended_action}</div>
              {r.draft_reply && (
                <div className="mt-3 bg-navy/5 border border-navy/10 p-3 text-sm text-navy whitespace-pre-wrap">
                  <div className="overline mb-2">Suggested reply</div>
                  {r.draft_reply}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
